import * as React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box
} from '@mui/material';
import { Close, Save } from '@mui/icons-material';

/**
 * Props for the SelectVerificationDialog component
 */
interface ConfirmActionDialogProps {
    /** Whether the dialog is open */
    open: boolean;
    /** The mode ('add' or 'ignore') */
    mode: string;
    /** Number of selected items */
    selectedCount: number;
    /** Callback when dialog is closed */
    onClose: () => void;
    /** Callback when save action is triggered with mode and status value */
    onSave: (mode: string, statusValue: number) => void;
    /** Optional prop for passing selected data rows if needed */
    dataRows?: any[];
}

/**
 * Shared Select Verification Dialog Component
 * Confirmation dialog for bulk verification status updates
 * Shows the number of selected items and confirmation for the action
 *
 * When mode === 'add': Displays dual-button interface for adding to queue
 * When mode === 'ignore': Displays dual-button interface for ignoring
 */
export const ConfirmActionDialog: React.FC<ConfirmActionDialogProps> = ({
    open,
    mode,
    selectedCount,
    onClose,
    onSave,
    dataRows
}) => {
    // Return if not open
    if (!open) return null;

    /**
     * Handles saving the selected events
     * Maps text values to numeric status values based on mode
     *
     * @param text - The selected text option
     */
    const handleSave = (): void => {
        onSave(mode, mode === 'add' ? 0 : -1); //-1: Ignored 0: Add new 1: Pending 2: Verified 3: Manual review
    };
   
    const handleClose = (): void => {
        onClose();
    };

    const title = mode === 'add'
        ? 'Add Selected Events to Uploading Queue'
        : 'Ignore Selected Events';

    const statusText: string[] = ['Add', 'Ignore'];
    const actionColor: Array<'success' | 'error'> = ['success', 'error'];

    const skipRowsCount = dataRows ? dataRows.filter(row => (row.tags || '').toLowerCase().includes('skip') ).length : 0;

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>{title}</DialogTitle>
            <DialogContent>
                <Box sx={{ py: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                        This action will update <strong>{selectedCount}</strong> selected records in a single operation.
                        {skipRowsCount > 0 && (
                            <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                                Note: <strong>{skipRowsCount}</strong> of the selected records contain "skip" in their tags and will be processed accordingly.
                            </Typography>
                        )}
                    </Typography>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} startIcon={<Close />}>
                    Cancel
                </Button>
                
                {/* Conditional rendering based on mode */}
                {mode === 'add' ? (
                // Add to Queue mode: Show confirm button for adding
                    <Button
                        onClick={handleSave}
                        startIcon={<Save />}
                        color={actionColor[0]}
                        variant="contained"
                    >
                        Confirm {statusText[0]}
                    </Button>
                    
                ) : (
                    // Verification mode: Show single confirm button
                    <Button
                        onClick={handleSave}
                        startIcon={<Save />}
                        color={actionColor[1]}
                        variant="contained"
                    >
                        Confirm {statusText[1]}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};
